import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import useDownloadPdf from "../../../hooks/useDownloadPdf";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, FileDown, Receipt, Edit2 } from "lucide-react";
import DocumentEditDialog from "./DocumentEditDialog";

const EMAIL_STATUS_LABEL = {
  sent: { label: "Verstuurd", cls: "bg-green-100 text-green-800" },
  skipped_no_key: { label: "Geen Resend key", cls: "bg-amber-100 text-amber-800" },
  skipped_no_recipient: { label: "Geen ontvanger", cls: "bg-gray-100 text-gray-700" },
  failed: { label: "Mislukt", cls: "bg-red-100 text-red-800" },
};

function StatusPill({ status }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const cfg = EMAIL_STATUS_LABEL[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-chivo font-bold px-2 py-0.5 ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

export default function InvoicesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [langFilter, setLangFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (emailFilter) params.set("email_copy_status", emailFilter);
      if (langFilter) params.set("language", langFilter);
      const r = await adminApi.get(`/admin/invoices?${params.toString()}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [emailFilter, langFilter]);

  // Phase 18.7 — shared PDF download UX (loading, slow-hint, toasts, de-dupe).
  const pdfDl = useDownloadPdf();
  const busy = pdfDl.loading;

  const downloadPdf = (id, invNo) =>
    pdfDl.download(
      async () => {
        const r = await adminApi.get(`/admin/invoices/${id}/pdf`, { responseType: "blob" });
        return {
          blob: new Blob([r.data], { type: "application/pdf" }),
          filename: `${invNo || "invoice"}.pdf`,
        };
      },
      {
        loadingMessage: "Factuur wordt gegenereerd…",
        successMessage: "Factuur klaar — download gestart.",
        errorMessage: "PDF kon niet worden gedownload. Probeer het opnieuw.",
        taskId: `invoice-pdf-${id}`,
      },
    );

  const totalsLine = useMemo(() => {
    if (!detail) return null;
    const t = detail.totals || {};
    return `${detail.currency || "EUR"} ${(t.total || 0).toFixed(2)}`;
  }, [detail]);

  return (
    <div className="space-y-4" data-testid="docs-invoices-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="docs-invoices-search"
          placeholder="Zoek op factuurnr/naam/email/W-nummer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && refresh()}
          className="rounded-none max-w-xs"
        />
        <select
          data-testid="docs-invoices-lang-filter"
          value={langFilter}
          onChange={(e) => setLangFilter(e.target.value)}
          className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10"
        >
          <option value="">Alle talen</option>
          <option value="nl">Nederlands</option>
          <option value="en">English</option>
        </select>
        <select
          data-testid="docs-invoices-email-filter"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10"
        >
          <option value="">E-mail kopie: alles</option>
          <option value="sent">Verstuurd</option>
          <option value="skipped_no_key">Geen key</option>
          <option value="failed">Mislukt</option>
        </select>
        <Button
          onClick={refresh}
          variant="outline"
          className="rounded-none"
          data-testid="docs-invoices-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-2" />Verversen
        </Button>
        <div className="ml-auto text-xs text-gray-500">{items.length} facturen</div>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider font-chivo font-bold text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Factuurnummer</th>
              <th className="px-3 py-2 text-left">Afzender</th>
              <th className="px-3 py-2 text-left">Bestemming</th>
              <th className="px-3 py-2 text-right">Totaal</th>
              <th className="px-3 py-2 text-left">E-mail kopie</th>
              <th className="px-3 py-2 text-left">Datum</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laden...
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-gray-500">Geen facturen gevonden.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`docs-invoice-row-${it.id}`} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-[12px] text-brand-900 font-bold">{it.invoice_number}</td>
                <td className="px-3 py-2">
                  <div>{it.sender?.name}</div>
                  <div className="text-xs text-gray-500">{it.sender?.email}</div>
                </td>
                <td className="px-3 py-2">{it.shipment?.destination || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {it.currency} {(it.totals?.total || 0).toFixed(2)}
                </td>
                <td className="px-3 py-2"><StatusPill status={it.email_copy_status} /></td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {it.created_at && new Date(it.created_at).toLocaleString("nl-NL")}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                    onClick={() => setDetail(it)}
                    data-testid={`docs-invoice-open-${it.id}`}
                  >
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl" data-testid="docs-invoice-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-brand-500" />
              {detail?.invoice_number}
            </DialogTitle>
            <DialogDescription>
              {detail?.created_at && new Date(detail.created_at).toLocaleString("nl-NL")}
              {detail && ` · ${detail.language?.toUpperCase()} · ${detail.currency}`}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-gray-100">
                <Button
                  size="sm"
                  className="rounded-none bg-[#082c50] hover:bg-[#1a5193] text-white"
                  disabled={busy}
                  onClick={() => downloadPdf(detail.id, detail.invoice_number)}
                  data-testid="docs-invoice-pdf"
                >
                  {busy
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PDF wordt gegenereerd...</>
                    : <><FileDown className="w-4 h-4 mr-2" /> PDF opnieuw downloaden</>}
                </Button>
                {busy && pdfDl.isSlow && (
                  <span className="text-xs text-gray-500" data-testid="docs-invoice-pdf-slow">
                    {pdfDl.slowHint}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  disabled={busy}
                  onClick={() => setEditOpen(true)}
                  data-testid="docs-invoice-edit"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Bewerken
                </Button>
                <StatusPill status={detail.email_copy_status} />
                {detail.email_copy_to && (
                  <span className="text-xs text-gray-500">→ {detail.email_copy_to}</span>
                )}
                {detail.email_copy_error && (
                  <span className="text-xs text-red-600 ml-2" data-testid="docs-invoice-email-error">
                    {detail.email_copy_error}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <PartyView title="Afzender" p={detail.sender} />
                <PartyView title="Ontvanger" p={detail.receiver} />
              </div>

              <section>
                <Label className="text-[10px] uppercase">Zending</Label>
                <dl className="mt-1 text-sm">
                  <div className="flex gap-2"><dt className="text-gray-500 w-32">Bestemming</dt><dd>{detail.shipment?.destination || "—"}</dd></div>
                  {detail.shipment?.w_number && <div className="flex gap-2"><dt className="text-gray-500 w-32">W-nummer</dt><dd>{detail.shipment.w_number}</dd></div>}
                  {detail.shipment?.boxes && <div className="flex gap-2"><dt className="text-gray-500 w-32">Aantal pakketten</dt><dd>{detail.shipment.boxes}</dd></div>}
                  {detail.shipment?.description && <div className="flex gap-2"><dt className="text-gray-500 w-32">Omschrijving</dt><dd>{detail.shipment.description}</dd></div>}
                  {detail.shipment?.remarks && <div className="flex gap-2"><dt className="text-gray-500 w-32">Opmerking</dt><dd>{detail.shipment.remarks}</dd></div>}
                </dl>
              </section>

              <section>
                <Label className="text-[10px] uppercase">Factuurregels</Label>
                <table className="w-full text-sm border border-gray-200 mt-1">
                  <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left px-2 py-1">Omschrijving</th>
                      <th className="text-right px-2 py-1">Aantal</th>
                      <th className="text-right px-2 py-1">Eenheid</th>
                      <th className="text-right px-2 py-1">Totaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.line_items || []).map((l, idx) => (
                      <tr key={idx} className="border-t border-gray-100">
                        <td className="px-2 py-1">{l.description}</td>
                        <td className="px-2 py-1 text-right font-mono">{l.quantity}</td>
                        <td className="px-2 py-1 text-right font-mono">{detail.currency} {(l.unit_price || 0).toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-mono">{detail.currency} {(l.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 text-sm">
                    <tr><td colSpan={3} className="px-2 py-1 text-right font-bold">Subtotaal</td><td className="px-2 py-1 text-right font-mono">{detail.currency} {(detail.totals?.subtotal || 0).toFixed(2)}</td></tr>
                    {detail.totals?.vat_percent ? (
                      <tr><td colSpan={3} className="px-2 py-1 text-right font-bold">BTW {detail.totals.vat_percent}%</td><td className="px-2 py-1 text-right font-mono">{detail.currency} {(detail.totals.vat_amount || 0).toFixed(2)}</td></tr>
                    ) : null}
                    <tr><td colSpan={3} className="px-2 py-1 text-right font-bold text-brand-900">Totaal</td><td className="px-2 py-1 text-right font-mono font-bold text-brand-900">{totalsLine}</td></tr>
                  </tfoot>
                </table>
              </section>

              <section>
                <Label className="text-[10px] uppercase">Verklaringen</Label>
                <ul className="mt-1 text-sm space-y-1">
                  <li>{detail.declarations?.value_declaration ? "✅" : "❌"} Waardeverklaring</li>
                  <li>{detail.declarations?.truthful ? "✅" : "❌"} Naar waarheid ingevuld</li>
                </ul>
              </section>

              {detail.disclaimer_text_used && (
                <section>
                  <Label className="text-[10px] uppercase">Gebruikte disclaimer (snapshot)</Label>
                  <pre className="mt-1 text-[11px] whitespace-pre-wrap bg-gray-50 border border-gray-200 p-3 text-gray-700 max-h-40 overflow-y-auto" data-testid="docs-invoice-disclaimer">
                    {detail.disclaimer_text_used}
                  </pre>
                </section>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDetail(null)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage A — Factuur / waardeverklaring bewerken */}
      {detail && (
        <DocumentEditDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          docType="generated_invoice"
          doc={detail}
          onSaved={(updated) => {
            setDetail((prev) => ({ ...prev, ...updated }));
            setItems((arr) => arr.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
          }}
        />
      )}
    </div>
  );
}

function PartyView({ title, p }) {
  if (!p) return null;
  return (
    <div>
      <Label className="text-[10px] uppercase">{title}</Label>
      <div className="mt-1 text-sm space-y-0.5">
        <div className="font-bold">{p.name}{p.company ? ` · ${p.company}` : ""}</div>
        <div>{p.address}</div>
        <div>{[p.postcode, p.city].filter(Boolean).join(" ")}</div>
        <div>{p.country}</div>
        {p.email && <div className="text-gray-500 text-xs">{p.email}</div>}
        {p.phone && <div className="text-gray-500 text-xs">{p.phone}</div>}
      </div>
    </div>
  );
}
